import { useEffect, useMemo, useState } from 'react';
import { Activity, Search } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { getSystemLogs } from '../api/systemLogs';
import { getUsers } from '../api/users';
import { toast } from 'sonner';
import ReferenceLookupModal from '../components/ReferenceLookupModal';

const SENSITIVE_METADATA_KEY_PATTERN = /(otp|password|token|secret|authorization|cookie|session)/i;

const sanitizeMetadataForDisplay = (value) => {
  if (Array.isArray(value)) {
    return value.map((item) => sanitizeMetadataForDisplay(item));
  }

  if (value && typeof value === 'object') {
    return Object.entries(value).reduce((acc, [key, nestedValue]) => {
      if (SENSITIVE_METADATA_KEY_PATTERN.test(key)) {
        acc[key] = '[REDACTED]';
      } else {
        acc[key] = sanitizeMetadataForDisplay(nestedValue);
      }
      return acc;
    }, {});
  }

  return value;
};

const ACTIVITY_SHORTCUTS = [
  { id: 'otp', label: 'OTP Flow', action: 'auth.otp_request' },
  { id: 'otpVerify', label: 'OTP Verify', action: 'auth.otp_verify.success' },
  { id: 'login', label: 'Login Success', action: 'auth.login.success' },
  { id: 'authFailed', label: 'Login/OTP Failures', query: 'auth.login.failed' },
  { id: 'ticketCreate', label: 'Ticket Created', action: 'ticket.created' },
  { id: 'ticketAssign', label: 'Ticket Assignment', query: 'ticket.assigned' },
  { id: 'ticketAssignFail', label: 'Assign Failures', action: 'ticket.assign.failed' },
  { id: 'ticketStatus', label: 'Ticket Status', action: 'ticket.status_changed' },
  { id: 'pageView', label: 'Page Activity', action: 'page.view' }
];

const SuperAdminLogs = () => {
  const [searchParams] = useSearchParams();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [level, setLevel] = useState('');
  const [action, setAction] = useState('');
  const [tenantId, setTenantId] = useState('');
  const [userId, setUserId] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 0 });
  const [lookupOpen, setLookupOpen] = useState(false);
  const [callerOptions, setCallerOptions] = useState([]);
  const [selectedCaller, setSelectedCaller] = useState(null);

  const fetchLogs = async (nextPage = page, overrides = {}) => {
    const filters = {
      query,
      level,
      action,
      tenantId,
      userId,
      fromDate,
      toDate,
      ...overrides
    };

    try {
      setLoading(true);
      const res = await getSystemLogs({
        page: nextPage,
        limit: 50,
        q: filters.query,
        level: filters.level,
        action: filters.action,
        tenant_id: filters.tenantId,
        user_id: filters.userId,
        from: filters.fromDate ? `${filters.fromDate} 00:00:00` : '',
        to: filters.toDate ? `${filters.toDate} 23:59:59` : '',
      });
      setLogs(Array.isArray(res?.data?.logs) ? res.data.logs : []);
      setPagination(res?.data?.pagination || { total: 0, totalPages: 0 });
      setPage(nextPage);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to load system logs');
      setLogs([]);
      setPagination({ total: 0, totalPages: 0 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const nextFilters = {
      query: searchParams.get('q') || '',
      level: searchParams.get('level') || '',
      action: searchParams.get('action') || '',
      tenantId: searchParams.get('tenant_id') || '',
      userId: searchParams.get('user_id') || '',
      fromDate: searchParams.get('from') || '',
      toDate: searchParams.get('to') || '',
    };

    setQuery(nextFilters.query);
    setLevel(nextFilters.level);
    setAction(nextFilters.action);
    setTenantId(nextFilters.tenantId);
    setUserId(nextFilters.userId);
    setFromDate(nextFilters.fromDate);
    setToDate(nextFilters.toDate);

    fetchLogs(1, nextFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const openCallerLookup = async () => {
    try {
      if (callerOptions.length === 0) {
        const res = await getUsers();
        if (res?.success && Array.isArray(res.data)) {
          setCallerOptions(res.data);
        } else {
          setCallerOptions([]);
        }
      }
      setLookupOpen(true);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to load callers');
    }
  };

  const onFilterSubmit = (e) => {
    e.preventDefault();
    fetchLogs(1);
  };

  const workflowSummary = useMemo(() => {
    const actionCounts = logs.reduce((acc, log) => {
      const actionKey = String(log?.action || '').toLowerCase();
      if (!actionKey) return acc;
      acc[actionKey] = (acc[actionKey] || 0) + 1;
      return acc;
    }, {});

    return {
      otpRequested: actionCounts['auth.otp_request'] || 0,
      otpVerified: actionCounts['auth.otp_verify.success'] || 0,
      loginSuccess: actionCounts['auth.login.success'] || 0,
      loginFailed: actionCounts['auth.login.failed'] || 0,
      ticketCreated: actionCounts['ticket.created'] || 0,
      ticketAssigned: (actionCounts['ticket.assigned'] || 0) + (actionCounts['ticket.unassigned'] || 0),
      ticketAssignFailed: actionCounts['ticket.assign.failed'] || 0,
      ticketStatusChanged: actionCounts['ticket.status_changed'] || 0,
      pageViews: actionCounts['page.view'] || 0
    };
  }, [logs]);

  const applyShortcut = (shortcut) => {
    const nextAction = shortcut.action || '';
    const nextQuery = shortcut.query || '';
    setAction(nextAction);
    setQuery(nextQuery);
    fetchLogs(1, { action: nextAction, query: nextQuery });
  };

  let tableRows;
  if (loading) {
    tableRows = (
      <tr>
        <td colSpan="7" className="px-4 py-8 text-center text-slate-500">Loading logs...</td>
      </tr>
    );
  } else if (logs.length === 0) {
    tableRows = (
      <tr>
        <td colSpan="7" className="px-4 py-8 text-center text-slate-500">No logs found.</td>
      </tr>
    );
  } else {
    tableRows = logs.map((log) => {
      const fallbackTenantId = log.tenant_id || log.resolved_tenant_id || log.metadata?.resolved_tenant_id || null;
      const tenantLabel = log.tenant_name || (fallbackTenantId ? `Tenant #${fallbackTenantId}` : 'No tenant');
      return (
      <tr key={log.id} className="hover:bg-slate-50/60 dark:hover:bg-slate-900/40">
        <td className="px-4 py-3 text-slate-500 whitespace-nowrap">
          {new Date(log.created_at).toLocaleString()}
        </td>
        <td className="px-4 py-3">
          <span className="px-2 py-1 rounded text-[10px] font-bold uppercase bg-slate-100 dark:bg-slate-700">
            {log.level}
          </span>
        </td>
        <td className="px-4 py-3">
          <span className="px-2 py-1 bg-slate-100 dark:bg-slate-700 rounded text-[10px] font-bold uppercase">
            {log.action}
          </span>
        </td>
        <td className="px-4 py-3">
          <div className="font-medium">{log.user_name || 'System'}</div>
          <div className="text-[11px] text-slate-400">{log.user_email || '—'} | {tenantLabel}</div>
        </td>
        <td className="px-4 py-3 text-xs text-slate-500">{log.ip_address || '—'}</td>
        <td className="px-4 py-3 text-slate-600 dark:text-slate-300 max-w-xs">{log.message}</td>
        <td className="px-4 py-3">
          <details className="cursor-pointer">
            <summary className="text-indigo-600 hover:underline text-xs">View JSON</summary>
            <pre className="mt-2 p-2 bg-slate-50 dark:bg-slate-900 text-[10px] rounded border border-slate-200 dark:border-slate-700 overflow-auto max-w-xs">
              {JSON.stringify(sanitizeMetadataForDisplay(log.metadata || {}), null, 2)}
            </pre>
          </details>
        </td>
      </tr>
      );
    });
  }

  return (
    <DashboardLayout userRole="super_admin">
      <div className="w-full min-h-0 bg-slate-50 dark:bg-slate-900 pb-2">
        <div className="w-full max-w-none mx-auto space-y-6">
          <div className="rounded-2xl tenant-brand-gradient text-white p-6 shadow-lg shadow-indigo-500/20">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur-md flex items-center justify-center border border-white/30">
                <Activity className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold tracking-tight">Endpoint System Activity</h1>
                <p className="text-white/80 text-sm mt-1">Persistent audit trail for login, OTP, page and ticket events.</p>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl p-4">
            <div className="mb-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-2">Activity shortcuts</p>
              <div className="flex flex-wrap gap-2">
                {ACTIVITY_SHORTCUTS.map((shortcut) => (
                  <button
                    key={shortcut.id}
                    type="button"
                    onClick={() => applyShortcut(shortcut)}
                    className="px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 text-xs font-semibold hover:bg-slate-100 dark:hover:bg-slate-800"
                  >
                    {shortcut.label}
                  </button>
                ))}
              </div>
            </div>
            <form onSubmit={onFilterSubmit} className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search action/message/user/tenant"
                className="border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 text-sm bg-slate-50 dark:bg-slate-900"
              />
              <select
                value={level}
                onChange={(e) => setLevel(e.target.value)}
                className="border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 text-sm bg-slate-50 dark:bg-slate-900"
              >
                <option value="">All levels</option>
                <option value="info">Info</option>
                <option value="success">Success</option>
                <option value="warn">Warn</option>
                <option value="error">Error</option>
              </select>
              <input
                value={action}
                onChange={(e) => setAction(e.target.value)}
                placeholder="Action (e.g. ticket.status_changed)"
                className="border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 text-sm bg-slate-50 dark:bg-slate-900"
              />
              <input
                value={tenantId}
                onChange={(e) => setTenantId(e.target.value)}
                placeholder="Tenant ID"
                className="border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 text-sm bg-slate-50 dark:bg-slate-900"
              />
              <input
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                placeholder="User ID"
                className="border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 text-sm bg-slate-50 dark:bg-slate-900"
              />
              <button
                type="button"
                onClick={openCallerLookup}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm font-semibold hover:bg-slate-50 dark:hover:bg-slate-800"
              >
                <Search className="w-4 h-4" />
                Caller Search
              </button>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 text-sm bg-slate-50 dark:bg-slate-900"
              />
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="border border-slate-200 dark:border-slate-700 rounded-xl px-3 py-2 text-sm bg-slate-50 dark:bg-slate-900"
              />
              <button type="submit" className="px-4 py-2 rounded-xl bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700">
                Apply
              </button>
              <button
                type="button"
                onClick={() => {
                  setQuery('');
                  setLevel('');
                  setAction('');
                  setTenantId('');
                  setUserId('');
                  setFromDate('');
                  setToDate('');
                  fetchLogs(1);
                }}
                className="px-4 py-2 rounded-xl bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 text-sm font-semibold hover:opacity-90"
              >
                Clear
              </button>
            </form>
            {selectedCaller && (
              <div className="mt-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 px-3 py-2 text-xs text-slate-600 dark:text-slate-300">
                <span className="font-bold text-slate-700 dark:text-slate-200">Selected Caller:</span>{' '}
                {selectedCaller.name || 'N/A'} | {selectedCaller.email || 'N/A'} | {selectedCaller.mobile_phone || 'N/A'} | {selectedCaller.job_title || 'N/A'} | tenant {selectedCaller.tenant_id || 'N/A'}
              </div>
            )}
            <div className="mt-4 grid grid-cols-2 md:grid-cols-4 xl:grid-cols-9 gap-2">
              <div className="rounded-xl border border-slate-200 dark:border-slate-700 px-3 py-2">
                <div className="text-[10px] uppercase text-slate-400">OTP Requested</div>
                <div className="text-sm font-bold">{workflowSummary.otpRequested}</div>
              </div>
              <div className="rounded-xl border border-slate-200 dark:border-slate-700 px-3 py-2">
                <div className="text-[10px] uppercase text-slate-400">OTP Verified</div>
                <div className="text-sm font-bold">{workflowSummary.otpVerified}</div>
              </div>
              <div className="rounded-xl border border-slate-200 dark:border-slate-700 px-3 py-2">
                <div className="text-[10px] uppercase text-slate-400">Login Success</div>
                <div className="text-sm font-bold">{workflowSummary.loginSuccess}</div>
              </div>
              <div className="rounded-xl border border-slate-200 dark:border-slate-700 px-3 py-2">
                <div className="text-[10px] uppercase text-slate-400">Login Failed</div>
                <div className="text-sm font-bold">{workflowSummary.loginFailed}</div>
              </div>
              <div className="rounded-xl border border-slate-200 dark:border-slate-700 px-3 py-2">
                <div className="text-[10px] uppercase text-slate-400">Tickets Created</div>
                <div className="text-sm font-bold">{workflowSummary.ticketCreated}</div>
              </div>
              <div className="rounded-xl border border-slate-200 dark:border-slate-700 px-3 py-2">
                <div className="text-[10px] uppercase text-slate-400">Assignments</div>
                <div className="text-sm font-bold">{workflowSummary.ticketAssigned}</div>
              </div>
              <div className="rounded-xl border border-slate-200 dark:border-slate-700 px-3 py-2">
                <div className="text-[10px] uppercase text-slate-400">Assign Failed</div>
                <div className="text-sm font-bold">{workflowSummary.ticketAssignFailed}</div>
              </div>
              <div className="rounded-xl border border-slate-200 dark:border-slate-700 px-3 py-2">
                <div className="text-[10px] uppercase text-slate-400">Status Changed</div>
                <div className="text-sm font-bold">{workflowSummary.ticketStatusChanged}</div>
              </div>
              <div className="rounded-xl border border-slate-200 dark:border-slate-700 px-3 py-2">
                <div className="text-[10px] uppercase text-slate-400">Page Views</div>
                <div className="text-sm font-bold">{workflowSummary.pageViews}</div>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl overflow-hidden shadow-sm">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-slate-50 dark:bg-slate-900/50 border-b border-slate-200 dark:border-slate-700">
                  <tr>
                    <th className="px-4 py-3 font-bold">Timestamp</th>
                    <th className="px-4 py-3 font-bold">Level</th>
                    <th className="px-4 py-3 font-bold">Action</th>
                    <th className="px-4 py-3 font-bold">User/Tenant</th>
                    <th className="px-4 py-3 font-bold">IP</th>
                    <th className="px-4 py-3 font-bold">Details</th>
                    <th className="px-4 py-3 font-bold">Metadata</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                  {tableRows}
                </tbody>
              </table>
            </div>
          </div>
          <div className="flex items-center justify-between text-sm text-slate-600 dark:text-slate-300">
            <span>Total logs: {pagination.total || 0}</span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={loading || page <= 1}
                onClick={() => fetchLogs(page - 1)}
                className="px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 disabled:opacity-50"
              >
                Prev
              </button>
              <span>Page {page} / {pagination.totalPages || 1}</span>
              <button
                type="button"
                disabled={loading || page >= (pagination.totalPages || 1)}
                onClick={() => fetchLogs(page + 1)}
                className="px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
      <ReferenceLookupModal
        isOpen={lookupOpen}
        onClose={() => setLookupOpen(false)}
        onSelect={(item) => {
          setSelectedCaller(item || null);
          setUserId(item?.id ? String(item.id) : '');
        }}
        title="Select Caller"
        data={callerOptions}
        displayKey="name"
        columns={[
          { key: 'name', label: 'Name', width: '25%' },
          { key: 'email', label: 'Email', width: '25%' },
          { key: 'mobile_phone', label: 'Phone', width: '15%' },
          { key: 'job_title', label: 'Title', width: '20%' },
          { key: 'tenant_id', label: 'Tenant', width: '15%' }
        ]}
      />
    </DashboardLayout>
  );
};

export default SuperAdminLogs;

